import React, { useState, useMemo, useCallback, useRef } from 'react';
import { useDrop, ConnectDragSource } from 'react-dnd';
import {
  MagnifyingGlassIcon,
  PaperPlaneTiltIcon,
  UserIcon,
  UserMinusIcon,
  TruckIcon,
  DownloadSimpleIcon,
} from '@phosphor-icons/react';
import classNames from '../../../../utils/classnames';
import { getCountryFlag } from '../../../../utils/country-flag';

import { Button } from '../../../../components/button/Button';
import toast from 'react-hot-toast';
import {
  PlanningBoardTruck,
  PlanningBoardTruckOrder,
  PlanningBoardBacklogOrder,
  updatePlanningBoardOrderStatus,
  updatePlanningBoardOrderKm,
  reorderPlanningBoardOrders,
  updatePlanningBoardTruckDriver,
} from '../../models';
import { getDisplayError } from '../../../../utils/get-display-error';
import { OrderCard } from './OrderCard';
import { SendOrderDialog } from './SendOrderDialog';
import { SuggestLoadsDialog } from './SuggestLoadsDialog';
import type { DragItem } from '../PlanningBoardPage';
import {
  getTrailerTypeLabel,
  formatCurrency,
  getSendableTruckOrders,
  getTruckDriverDisplayName,
  isPlanningBoardResend,
} from '../../utils';

// ============================================================================
// Types
// ============================================================================

interface TruckCardProps {
  truck: PlanningBoardTruck;
  date: string;
  onOrderDrop: (order: PlanningBoardBacklogOrder | PlanningBoardTruckOrder, sourceTruckId: string | null) => void;
  onRefresh: () => void;
  headerDragRef?: ConnectDragSource;
}

// ============================================================================
// Helper functions
// ============================================================================

function calculateTruckMetrics(orders: PlanningBoardTruckOrder[]) {
  let totalLoadedKm = 0;
  let emptyKm = 0;
  let totalSales = 0;
  let totalPurchases = 0;
  let sentCount = 0;

  for (const order of orders) {
    if (order.manualFullKm != null) {
      totalLoadedKm += order.manualFullKm;
    } else {
      for (const line of order.lines) {
        totalLoadedKm += line.distance / 1000; // Convert meters to km
      }
    }

    emptyKm += order.manualEmptyKm ?? order.autoEmptyKm ?? 0;

    totalSales += order.saleTotal;
    totalPurchases += order.purchaseTotal;

    if (order.transportOrderSentAt) {
      sentCount++;
    }
  }

  const totalKm = emptyKm + totalLoadedKm;
  const emptyPercentage = totalKm > 0 ? Math.round((emptyKm / totalKm) * 100) : 0;
  const margin = totalSales - totalPurchases;
  const marginPercentage = totalSales > 0 ? Math.round((margin / totalSales) * 100) : 0;

  return {
    totalEmptyKm: Math.round(emptyKm),
    totalLoadedKm: Math.round(totalLoadedKm),
    totalKm: Math.round(totalKm),
    emptyPercentage,
    totalSales,
    totalPurchases,
    margin,
    marginPercentage,
    sentCount,
  };
}

// ============================================================================
// ReorderableOrderCard - always-present drop zone with cursor position detection
// ============================================================================

interface ReorderableOrderCardProps {
  truckId: string;
  index: number;
  isLast: boolean;
  onReorder: (orderId: string, toInsertBefore: number) => void;
  children: React.ReactNode;
}

const ReorderableOrderCard: React.FC<ReorderableOrderCardProps> = ({ truckId, index, isLast, onReorder, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [hoverSide, setHoverSide] = useState<'top' | 'bottom'>('top');
  const hoverSideRef = useRef<'top' | 'bottom'>('top');

  const [{ isOver, canDrop }, drop] = useDrop<DragItem, void, { isOver: boolean; canDrop: boolean }>(
    () => ({
      accept: 'order',
      canDrop: (item) => item.sourceTruckId === truckId,
      hover: (_item, monitor) => {
        if (!ref.current || !monitor.canDrop()) return;
        const rect = ref.current.getBoundingClientRect();
        const clientOffset = monitor.getClientOffset();
        if (!clientOffset) return;
        const side = clientOffset.y - rect.top < rect.height / 2 ? 'top' : 'bottom';
        if (side !== hoverSideRef.current) {
          hoverSideRef.current = side;
          setHoverSide(side);
        }
      },
      drop: (item, monitor) => {
        if (!ref.current || monitor.didDrop()) return;
        const rect = ref.current.getBoundingClientRect();
        const clientOffset = monitor.getClientOffset();
        if (!clientOffset) return;
        const insertBefore = clientOffset.y - rect.top < rect.height / 2 ? index : index + 1;
        onReorder(item.order.id, insertBefore);
      },
      collect: (monitor) => ({
        isOver: monitor.isOver({ shallow: true }),
        canDrop: monitor.canDrop(),
      }),
    }),
    [truckId, index, onReorder],
  );

  const showIndicator = isOver && canDrop;

  return (
    <div
      ref={(node) => {
        ref.current = node;
        drop(node);
      }}
      className="relative"
    >
      {showIndicator && hoverSide === 'top' && (
        <div className="absolute left-0 right-0 h-1 rounded-full z-10 pointer-events-none bg-brand -top-1.5" />
      )}
      {showIndicator && hoverSide === 'bottom' && isLast && (
        <div className="absolute left-0 right-0 h-1 rounded-full z-10 pointer-events-none bg-brand -bottom-1.5" />
      )}
      {children}
    </div>
  );
};

// ============================================================================
// ReorderTailDrop - drop zone below the last order card
// ============================================================================

interface ReorderTailDropProps {
  truckId: string;
  insertIndex: number;
  onReorder: (orderId: string, toInsertBefore: number) => void;
}

const ReorderTailDrop: React.FC<ReorderTailDropProps> = ({ truckId, insertIndex, onReorder }) => {
  const [{ isOver, canDrop }, drop] = useDrop<DragItem, void, { isOver: boolean; canDrop: boolean }>(
    () => ({
      accept: 'order',
      canDrop: (item) => item.sourceTruckId === truckId,
      drop: (item, monitor) => {
        if (!monitor.didDrop()) {
          onReorder(item.order.id, insertIndex);
        }
      },
      collect: (monitor) => ({
        isOver: monitor.isOver({ shallow: true }),
        canDrop: monitor.canDrop(),
      }),
    }),
    [truckId, insertIndex, onReorder],
  );

  return (
    <div
      ref={(node) => {
        drop(node);
      }}
      className={classNames(
        'h-4 rounded-md transition-colors',
        isOver && canDrop ? 'border border-dashed border-brand bg-brand-subtle' : '',
      )}
    />
  );
};

// ============================================================================
// Component
// ============================================================================

const HEADER_ICON_BUTTON =
  'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-fg-on-inverse-muted transition-colors hover:bg-white/15 hover:text-fg-inverse';

export const TruckCard: React.FC<TruckCardProps> = ({ truck, date, onOrderDrop, onRefresh, headerDragRef }) => {
  const [isSendDialogOpen, setIsSendDialogOpen] = useState(false);
  const [isSuggestDialogOpen, setIsSuggestDialogOpen] = useState(false);
  const [isEditingDriver, setIsEditingDriver] = useState(false);
  const [driverDraft, setDriverDraft] = useState('');
  const [isSavingDriver, setIsSavingDriver] = useState(false);

  const driverDisplayName = getTruckDriverDisplayName(truck);

  const handleDriverSave = useCallback(async () => {
    const trimmed = driverDraft.trim();
    if (trimmed === truck.driverDisplayName.trim()) {
      setIsEditingDriver(false);
      return;
    }
    setIsSavingDriver(true);
    try {
      await updatePlanningBoardTruckDriver({ truckId: truck.id, driverDisplayName: trimmed });
      onRefresh();
      setIsEditingDriver(false);
      toast.success('Chauffeursnaam opgeslagen');
    } catch (err) {
      toast.error(`Kon chauffeursnaam niet opslaan: ${getDisplayError(err)}`);
    } finally {
      setIsSavingDriver(false);
    }
  }, [driverDraft, truck.id, truck.driverDisplayName, onRefresh]);

  // Drop zone for orders
  const [{ isOver, canDrop }, drop] = useDrop<DragItem, void, { isOver: boolean; canDrop: boolean }>(
    () => ({
      accept: 'order',
      canDrop: (item) => {
        // Can't drop if already on this truck
        return item.sourceTruckId !== truck.id;
      },
      drop: (item) => {
        onOrderDrop(item.order, item.sourceTruckId);
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [truck.id, onOrderDrop],
  );

  const metrics = useMemo(() => calculateTruckMetrics(truck.orders), [truck.orders]);

  const sendableOrders = useMemo(() => getSendableTruckOrders(truck.orders, date), [truck.orders, date]);

  const handleStatusChange = useCallback(
    async (orderId: string, status: string) => {
      try {
        await updatePlanningBoardOrderStatus({ orderId, status });
        onRefresh();
        toast.success('Status bijgewerkt');
      } catch (err) {
        toast.error(`Kon status niet bijwerken: ${getDisplayError(err)}`);
      }
    },
    [onRefresh],
  );

  const handleKmChange = useCallback(
    async (orderId: string, manualFullKm: number | null, manualEmptyKm: number | null) => {
      try {
        await updatePlanningBoardOrderKm({ orderId, manualFullKm, manualEmptyKm });
        onRefresh();
        toast.success('Kilometers opgeslagen');
      } catch (err) {
        toast.error(`Kon kilometers niet opslaan: ${getDisplayError(err)}`);
      }
    },
    [onRefresh],
  );

  const handleSendDialogClose = useCallback(() => setIsSendDialogOpen(false), []);
  const handleSendSuccess = useCallback(() => {
    setIsSendDialogOpen(false);
    onRefresh();
  }, [onRefresh]);

  const handleDownloadPdf = useCallback(() => {
    const browserWindow = globalThis as Window & typeof globalThis;
    const url = new URL(
      `/api/planning/export-document?date=${date}&truckId=${encodeURIComponent(truck.id)}&orderIds=${encodeURIComponent(truck.orders.map((o) => o.id).join(';'))}`,
      browserWindow.location.href,
    );
    browserWindow.open(url.toString());
  }, [date, truck.id, truck.orders]);

  const handleReorder = useCallback(
    async (orderId: string, toInsertBefore: number) => {
      const fromIndex = truck.orders.findIndex((o) => o.id === orderId);
      if (fromIndex === -1 || fromIndex === toInsertBefore || fromIndex === toInsertBefore - 1) return;

      const reordered = [...truck.orders];
      const [moved] = reordered.splice(fromIndex, 1);
      reordered.splice(toInsertBefore > fromIndex ? toInsertBefore - 1 : toInsertBefore, 0, moved);

      try {
        await reorderPlanningBoardOrders({
          supplierTruckId: truck.id,
          date,
          orderSequences: reordered.map((o, i) => ({ orderId: o.id, sequence: i + 1 })),
        });
        onRefresh();
      } catch (err) {
        toast.error(`Kon volgorde niet opslaan: ${getDisplayError(err)}`);
      }
    },
    [truck, date, onRefresh],
  );

  // Truck's country from the license plate prefix (only shown when it is a known code)
  const truckFlag = getCountryFlag(truck.licensePlate?.substring(0, 2));
  const locationFlag = getCountryFlag(truck.currentLocation?.country);
  const orderCount = truck.orders.length;

  return (
    <div
      ref={(node) => {
        drop(node);
      }}
      className={classNames(
        'flex h-full flex-col overflow-visible rounded-lg border border-border bg-surface',
        isOver && canDrop && 'ring-2 ring-brand',
        isOver && !canDrop && 'ring-2 ring-negative',
      )}
    >
      {/* Header */}
      <div
        ref={headerDragRef as unknown as React.Ref<HTMLDivElement>}
        className={classNames(
          'flex items-start justify-between gap-2.5 rounded-t-lg bg-surface-inverse px-3 py-2',
          headerDragRef && 'cursor-grab',
        )}
      >
        {/* Left: license plate and driver */}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-fg-inverse">{truck.licensePlate}</p>
          <div className="flex min-w-0 items-center gap-1 text-2xs text-fg-on-inverse-muted">
            {driverDisplayName || isEditingDriver ? (
              <UserIcon className="h-3 w-3 shrink-0" />
            ) : (
              <UserMinusIcon className="h-3 w-3 shrink-0 text-brand-border" />
            )}
            {isEditingDriver ? (
              <input
                type="text"
                className="min-w-0 flex-1 rounded border border-border-strong bg-fg-secondary px-1 py-0.5 text-2xs text-fg-inverse focus:border-brand focus:outline-hidden"
                value={driverDraft}
                onChange={(e) => setDriverDraft(e.target.value)}
                onBlur={() => void handleDriverSave()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void handleDriverSave();
                  }
                  if (e.key === 'Escape') {
                    setIsEditingDriver(false);
                  }
                }}
                disabled={isSavingDriver}
                autoFocus
                placeholder="Chauffeursnaam"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              />
            ) : (
              <button
                type="button"
                className="truncate text-left transition-colors hover:text-fg-inverse"
                title="Klik om chauffeursnaam te bewerken"
                onClick={(e) => {
                  e.stopPropagation();
                  setDriverDraft(truck.driverDisplayName);
                  setIsEditingDriver(true);
                }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                {driverDisplayName || 'Geen chauffeur'}
              </button>
            )}
          </div>
        </div>

        {/* Right: actions, trailer type, current location */}
        <div className="flex shrink-0 flex-col items-end gap-0.5">
          <div className="flex items-center gap-1.5 text-2xs text-fg-on-inverse-muted">
            {orderCount > 0 && (
              <button
                type="button"
                className={HEADER_ICON_BUTTON}
                title="Download planning (PDF)"
                aria-label="Download planning (PDF)"
                onClick={handleDownloadPdf}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <DownloadSimpleIcon className="h-3.5 w-3.5" />
              </button>
            )}
            <button
              type="button"
              className={HEADER_ICON_BUTTON}
              title="Zoek beste retourlading"
              aria-label="Zoek beste retourlading"
              onClick={() => setIsSuggestDialogOpen(true)}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <MagnifyingGlassIcon className="h-3.5 w-3.5" />
            </button>
            {truckFlag && <span className="text-xs leading-none">{truckFlag}</span>}
            {truck.defaultTrailerType && (
              <span className="font-semibold text-fg-inverse" title="Trailertype">
                {getTrailerTypeLabel(truck.defaultTrailerType)}
              </span>
            )}
          </div>
          {truck.currentLocation && (
            <div className="flex items-center gap-1 text-2xs text-fg-on-inverse-muted" title="Huidige locatie">
              {locationFlag && <span className="leading-none">{locationFlag}</span>}
              <span className="truncate">{truck.currentLocation.city}</span>
            </div>
          )}
        </div>
      </div>

      {/* Orders / Drop zone */}
      <div className="flex flex-1 flex-col gap-2 overflow-visible p-2.5">
        {orderCount === 0 ? (
          <div className="flex h-[66px] flex-col items-center justify-center gap-1 rounded-md border border-dashed border-border-strong bg-surface-sunken">
            <TruckIcon className="h-5 w-5 text-fg-muted" />
            <p className="text-2xs text-fg-muted">Sleep hier ladingen</p>
          </div>
        ) : (
          <>
            {truck.orders.map((order, index) => (
              <ReorderableOrderCard
                key={order.id}
                truckId={truck.id}
                index={index}
                isLast={index === orderCount - 1}
                onReorder={handleReorder}
              >
                <OrderCard
                  order={order}
                  sourceTruckId={truck.id}
                  onStatusChange={handleStatusChange}
                  onKmChange={handleKmChange}
                />
              </ReorderableOrderCard>
            ))}
            <ReorderTailDrop truckId={truck.id} insertIndex={orderCount} onReorder={handleReorder} />
          </>
        )}
      </div>

      {/* Footer: totals, financials, send */}
      {orderCount > 0 && (
        <div className="flex flex-col gap-1 rounded-b-lg border-t border-border bg-surface-sunken px-3 py-2 text-2xs">
          <div className="flex flex-wrap items-center gap-x-1.5 text-fg-secondary">
            <span className="font-semibold text-fg">
              {orderCount} {orderCount === 1 ? 'order' : 'orders'}
            </span>
            <span className="text-border-strong">·</span>
            <span>{metrics.totalLoadedKm} km vol</span>
            <span className="text-border-strong">/</span>
            <span className={classNames(metrics.totalEmptyKm > 0 && 'font-medium text-negative-text')}>
              {metrics.totalEmptyKm} km leeg
            </span>
            <span className="text-border-strong">/</span>
            <span>{metrics.totalKm} km totaal</span>
          </div>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
            <span>
              <span className="text-fg-muted">Verkoop </span>
              <span className="font-semibold text-fg">€{formatCurrency(metrics.totalSales)}</span>
            </span>
            <span>
              <span className="text-fg-muted">Aankoop </span>
              <span className="font-semibold text-fg">€{formatCurrency(metrics.totalPurchases)}</span>
            </span>
            <span>
              <span className="text-fg-muted">Marge </span>
              <span
                className={classNames(
                  'font-semibold',
                  metrics.margin >= 0 ? 'text-positive-text' : 'text-negative-text',
                )}
              >
                €{formatCurrency(metrics.margin)} ({metrics.marginPercentage}%)
              </span>
            </span>
          </div>

          {(metrics.sentCount > 0 || sendableOrders.length > 0) && (
            <div className="flex items-center justify-between gap-2 pt-1">
              <span className="text-fg-muted">
                {metrics.sentCount > 0 ? (
                  <>
                    <span className="font-semibold text-fg">{metrics.sentCount}</span> van {orderCount}{' '}
                    {orderCount === 1 ? 'order' : 'orders'} verstuurd
                  </>
                ) : (
                  'Nog niet verstuurd'
                )}
              </span>
              {sendableOrders.length > 0 && (
                <Button
                  color="primary"
                  size={8}
                  onTrigger={() => setIsSendDialogOpen(true)}
                  iconLeft={<PaperPlaneTiltIcon className="button-icon" />}
                >
                  {isPlanningBoardResend(sendableOrders) ? 'Opnieuw versturen' : 'Verstuur'} {sendableOrders.length}{' '}
                  {sendableOrders.length === 1 ? 'order' : 'orders'}
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Send Dialog - only mount when open to avoid unnecessary effects */}
      {isSendDialogOpen && (
        <SendOrderDialog
          isOpen={isSendDialogOpen}
          onClose={handleSendDialogClose}
          truck={truck}
          orders={sendableOrders}
          date={date}
          onSuccess={handleSendSuccess}
        />
      )}

      {/* Suggest Loads Dialog */}
      {isSuggestDialogOpen && (
        <SuggestLoadsDialog
          isOpen={isSuggestDialogOpen}
          onClose={() => setIsSuggestDialogOpen(false)}
          truck={truck}
          date={date}
          onSelectOrder={(order) => {
            onOrderDrop(order, null);
            setIsSuggestDialogOpen(false);
          }}
        />
      )}
    </div>
  );
};
